/**
 * Servicio de **entregables previos** por club para una actividad.
 *
 * Un director envía datos (formulario dinámico) y archivos antes de la
 * fecha límite definida en la actividad. El admin asociación luego revisa
 * y aprueba/rechaza.
 *
 * Colección: `clubActivitySubmissions`
 * Doc ID:    `${eventId}_${activityId}_${clubId}`
 *
 * Storage:   `events/{eventId}/submissions/{activityId}/{clubId}/{fileName}`
 */

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  where,
  Timestamp,
  increment,
  deleteField,
} from "firebase/firestore";
import { buildScoreId, upsertScore, deleteScore } from "./eventActivities";
import type { ActivityScoreInput, EventActivityCategory } from "@/types";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage, auth } from "@/lib/firebase";
import { subscribeToCollection } from "./base";
import type {
  ActivitySubmissionField,
  ClubActivitySubmission,
  EventActivity,
  SubmissionFieldValue,
  SubmissionFileRef,
  SubmissionStatus,
} from "@/types";

const COLLECTION = "clubActivitySubmissions";
const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15MB

const ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// ============================================================================
// Helpers
// ============================================================================

export function buildSubmissionId(
  eventId: string,
  activityId: string,
  clubId: string,
): string {
  return `${eventId}_${activityId}_${clubId}`;
}

/**
 * Normaliza un valor de fecha que puede venir como `Timestamp` (cuando se
 * acaba de escribir desde el cliente) o como `Date` (después de pasar por
 * `convertTimestamps` en `subscribeToCollection`).
 */
export function toJsDate(
  value: Timestamp | Date | string | { toDate: () => Date } | null | undefined,
): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate();
  }
  // Tolerar untilDate guardado como string ISO (algunos seeds/scripts lo
  // escribieron así). Sin esto, las bandas de puntaje con fecha-string no
  // resolvían y todas las entregas caían en "fuera de plazo" (0 pts).
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === undefined) return;
    out[k] = v;
  });
  return out as T;
}

// ============================================================================
// Subir archivos (no encriptado)
// ============================================================================

export async function uploadSubmissionFile(
  eventId: string,
  activityId: string,
  clubId: string,
  file: File,
): Promise<SubmissionFileRef> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("El archivo supera el tamaño máximo (15 MB).");
  }
  if (!file.type) {
    throw new Error(
      "No se pudo determinar el tipo del archivo. Use PDF, imagen, Word o Excel.",
    );
  }
  if (!ALLOWED_MIME.includes(file.type)) {
    throw new Error(
      `Tipo de archivo no permitido (${file.type}). Use PDF, imagen, Word o Excel.`,
    );
  }
  const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
  const stamp = Date.now();
  const path = `events/${eventId}/submissions/${activityId}/${clubId}/${stamp}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      uploaderUid: uid,
      eventId,
      activityId,
      clubId,
    },
  });
  const url = await getDownloadURL(storageRef);
  return {
    url,
    path,
    name: file.name,
    contentType: file.type,
    size: file.size,
    uploadedAt: Timestamp.now(),
  };
}

export async function deleteSubmissionFile(path: string): Promise<void> {
  try {
    await deleteObject(ref(storage, path));
  } catch (e) {
    // si ya no existe, ignoramos
    console.warn("[deleteSubmissionFile]", e);
  }
}

// ============================================================================
// Suscripciones
// ============================================================================

/** Todas las entregas de un evento (admin). */
export function subscribeToSubmissionsByEvent(
  eventId: string,
  cb: (items: ClubActivitySubmission[]) => void,
  onError?: (e: Error) => void,
): () => void {
  return subscribeToCollection<ClubActivitySubmission>(
    COLLECTION,
    cb,
    [where("eventId", "==", eventId)],
    onError,
  );
}

/** Entregas de un club específico para un evento (director). */
export function subscribeToSubmissionsByClub(
  eventId: string,
  clubId: string,
  cb: (items: ClubActivitySubmission[]) => void,
  onError?: (e: Error) => void,
): () => void {
  return subscribeToCollection<ClubActivitySubmission>(
    COLLECTION,
    cb,
    [where("eventId", "==", eventId), where("clubId", "==", clubId)],
    onError,
  );
}

// ============================================================================
// CRUD
// ============================================================================

export interface SaveSubmissionInput {
  eventId: string;
  activityId: string;
  activityName: string;
  categoryId: string;
  clubId: string;
  clubName: string;
  associationId?: string;
  values: Record<string, SubmissionFieldValue>;
  /** "draft" guarda sin marcarse como entregado; "submitted" marca como enviado. */
  status: Extract<SubmissionStatus, "draft" | "submitted">;
  /** Deadline efectivo (para calcular isLate). */
  deadline?: Timestamp;
  /** Bandas timing (para resolver timingBandIndex automáticamente). */
  timingBands?: { label: string; points: number; untilDate?: Timestamp }[];
  /** Si es la primera vez que se envía (vs editar). */
  isFirstSubmission: boolean;
}

export async function saveSubmission(
  input: SaveSubmissionInput,
): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");
  const userName =
    auth.currentUser?.displayName || auth.currentUser?.email || "";
  const id = buildSubmissionId(input.eventId, input.activityId, input.clubId);
  const ref_ = doc(db, COLLECTION, id);

  const now = Timestamp.now();
  const deadlineDate = toJsDate(input.deadline);
  const isLate =
    input.status === "submitted" && deadlineDate
      ? now.toMillis() > deadlineDate.getTime()
      : undefined;

  // Resolver timingBandIndex automáticamente según bandas con `untilDate`.
  // Reglas:
  //  1. Si alguna banda tiene `untilDate`, se elige la PRIMERA (en orden)
  //     cuyo `untilDate >= ahora`.
  //  2. Si ninguna banda con fecha aplica, se usa la última banda ("tarde").
  //  3. Si ninguna banda tiene `untilDate`, fallback al comportamiento
  //     antiguo basado en `deadline` (a tiempo → 0, tarde → última).
  let timingBandIndex: number | undefined;
  if (
    input.status === "submitted" &&
    input.timingBands &&
    input.timingBands.length > 0
  ) {
    const nowMs = now.toMillis();
    const hasDatedBands = input.timingBands.some((b) => b.untilDate);
    if (hasDatedBands) {
      const matchIdx = input.timingBands.findIndex((b) => {
        const ms = toJsDate(b.untilDate)?.getTime();
        return ms !== undefined && nowMs <= ms;
      });
      timingBandIndex = matchIdx >= 0 ? matchIdx : input.timingBands.length - 1;
    } else if (!deadlineDate) {
      timingBandIndex = 0;
    } else if (nowMs <= deadlineDate.getTime()) {
      timingBandIndex = 0;
    } else {
      // Si hay 3 bandas: [a tiempo, extra, tarde] — usamos la última.
      // El admin puede ajustar manualmente luego.
      timingBandIndex = input.timingBands.length - 1;
    }
  }

  const payload: Record<string, unknown> = stripUndefined({
    id,
    eventId: input.eventId,
    activityId: input.activityId,
    activityName: input.activityName,
    categoryId: input.categoryId,
    clubId: input.clubId,
    clubName: input.clubName,
    associationId: input.associationId,
    status: input.status,
    values: input.values,
    isLate,
    timingBandIndex,
    lastEditedById: uid,
    lastEditedByName: userName,
    lastEditedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  });

  // Auditoría inicial: en la primera escritura (sea draft o submitted) sembramos
  // createdAt/createdBy y reviewStatus 'pending'. En envíos posteriores
  // (re-enviar tras rechazo o editar tras enviar) incrementamos editCount.
  if (input.isFirstSubmission) {
    payload.createdAt = serverTimestamp();
    payload.createdBy = uid;
    payload.editCount = 0;
    payload.reviewStatus = "pending";
    if (input.status === "submitted") {
      payload.submittedById = uid;
      payload.submittedByName = userName;
      payload.submittedAt = serverTimestamp();
    }
  } else if (input.status === "submitted") {
    // Re-envío / edición tras envío: contar y volver a pending de revisión.
    payload.editCount = increment(1);
    payload.reviewStatus = "pending";
    payload.submittedById = uid;
    payload.submittedByName = userName;
    payload.submittedAt = serverTimestamp(); // #9: limpiar el feedback de la revisi\u00f3n previa para que el director no
    // siga viendo el motivo del rechazo despu\u00e9s de re-enviar.
    payload.reviewNotes = deleteField();
    payload.reviewedAt = deleteField();
    payload.reviewedById = deleteField();
    payload.reviewedByName = deleteField();
  }

  await setDoc(ref_, payload, { merge: true });
  return id;
}

export async function deleteSubmission(submissionId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, submissionId));
}

// ============================================================================
// Revisión por admin
// ============================================================================

export interface ReviewSubmissionInput {
  submissionId: string;
  decision: "approved" | "rejected";
  notes?: string;
  /** Permite al admin sobrescribir el timingBandIndex calculado. */
  timingBandIndexOverride?: number;
}

export async function reviewSubmission(
  input: ReviewSubmissionInput,
): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");
  const userName =
    auth.currentUser?.displayName || auth.currentUser?.email || "";
  const ref_ = doc(db, COLLECTION, input.submissionId);
  // Solo tocamos campos de revisión. NO cambiamos `status` (que pertenece al
  // flujo del director: draft/submitted). El admin actúa sobre `reviewStatus`.
  await setDoc(
    ref_,
    stripUndefined({
      reviewStatus: input.decision,
      reviewedAt: serverTimestamp(),
      reviewedById: uid,
      reviewedByName: userName,
      reviewNotes: input.notes,
      timingBandIndex: input.timingBandIndexOverride,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    }),
    { merge: true },
  );

  // ──────────────────────────────────────────────────────────────────────
  // Sincronización con `clubActivityScores`:
  //  - aprobado → upsert del score (suma puntos al ranking).
  //  - rechazado → eliminar el score si existía.
  // Se hace best-effort: un fallo aquí no debe revertir la revisión.
  // ──────────────────────────────────────────────────────────────────────
  try {
    const submSnap = await getDoc(ref_);
    if (!submSnap.exists()) return;
    const subm = submSnap.data() as ClubActivitySubmission;
    const scoreId = buildScoreId(subm.eventId, subm.activityId, subm.clubId);

    if (input.decision === "rejected") {
      await deleteScore(scoreId);
      return;
    }

    // Aprobado: cargar la actividad para conocer scoreMode + scoreConfig.
    const actSnap = await getDoc(doc(db, "eventActivities", subm.activityId));
    if (!actSnap.exists()) {
      console.warn(
        "[reviewSubmission] actividad no encontrada",
        subm.activityId,
      );
      return;
    }
    const act = actSnap.data() as EventActivity;

    // Construir el input de puntuación según el modo.
    const tbIdx =
      input.timingBandIndexOverride ?? subm.timingBandIndex ?? undefined;
    let scoreInput: ActivityScoreInput | null = null;
    switch (act.scoreMode) {
      case "fixed":
        scoreInput = { delivered: true };
        break;
      case "pass_fail":
        scoreInput = { passed: true };
        break;
      case "timing":
        if (typeof tbIdx === "number" && tbIdx >= 0) {
          scoreInput = { timingBandIndex: tbIdx };
        } else {
          console.warn(
            "[reviewSubmission] timing sin timingBandIndex; no se autoasigna score",
            scoreId,
          );
        }
        break;
      default:
        // podium / deductive / accumulative requieren captura manual del jurado.
        return;
    }
    if (!scoreInput) return;

    // Resolver categoryName (no está en la submission).
    let categoryName = "";
    try {
      const catSnap = await getDoc(
        doc(db, "eventActivityCategories", subm.categoryId),
      );
      if (catSnap.exists()) {
        categoryName = (catSnap.data() as EventActivityCategory).name ?? "";
      }
    } catch {
      // ignore
    }

    await upsertScore({
      eventId: subm.eventId,
      activityId: subm.activityId,
      activityName: subm.activityName,
      categoryId: subm.categoryId,
      categoryName,
      clubId: subm.clubId,
      clubName: subm.clubName,
      scoreMode: act.scoreMode,
      scoreConfig: act.scoreConfig,
      input: scoreInput,
      notes: "Auto: entrega aprobada",
      scoredByName: userName || "Sistema (entrega)",
    });
  } catch (e) {
    console.error("[reviewSubmission] sincronización de score falló", e);
  }
}

// ============================================================================
// Estado UI
// ============================================================================

/**
 * Determina si un director puede editar una entrega:
 *  - Existe y status !== "approved" / "rejected" (no fue revisada).
 *  - Si existe deadline y `allowLate === false`, debe ser antes del deadline.
 *  - Si no existe entrega aún → puede crear.
 */
export function canDirectorEdit(
  activity: EventActivity,
  submission: ClubActivitySubmission | undefined,
): { allowed: boolean; reason?: string } {
  const req = activity.submissionRequirements;
  if (!req || !req.enabled) {
    return { allowed: false, reason: "No requiere entrega" };
  }
  if (submission?.reviewStatus === "approved") {
    return { allowed: false, reason: "Ya fue aprobada por la asociación" };
  }
  if (submission?.reviewStatus === "rejected") {
    // Permitimos re-enviar tras un rechazo.
    return { allowed: true };
  }
  if (req.opensAt) {
    const now = Date.now();
    const opensMs = toJsDate(req.opensAt)?.getTime();
    if (opensMs !== undefined && now < opensMs) {
      const when = toJsDate(req.opensAt)?.toLocaleString("es-CO");
      return {
        allowed: false,
        reason: when
          ? `La entrega abre el ${when}`
          : "La entrega aún no está abierta",
      };
    }
  }
  if (req.deadline && req.allowLate !== true) {
    const now = Date.now();
    const deadlineMs = toJsDate(req.deadline)?.getTime();
    if (deadlineMs !== undefined && now > deadlineMs) {
      return { allowed: false, reason: "Pasó la fecha límite" };
    }
  }
  return { allowed: true };
}

/**
 * Valida que los valores cumplan con los `required` del formulario.
 * Devuelve la lista de errores por field.id.
 */
export function validateSubmissionValues(
  fields: ActivitySubmissionField[],
  values: Record<string, SubmissionFieldValue>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    if (!f.required) continue;
    const v = values[f.id];
    const empty =
      v === undefined ||
      v === null ||
      v === "" ||
      (Array.isArray(v) && v.length === 0);
    if (empty) {
      errors[f.id] = "Campo requerido";
    }
  }
  return errors;
}

/**
 * Genera un slug consistente para field.id basado en label.
 */
export function slugifyFieldId(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

// ============================================================================
// Plantillas predefinidas
// ============================================================================

/**
 * Plantilla: Informe Mensual de Conquistadores (basado en Google Form).
 */
export const TEMPLATE_INFORME_MENSUAL: ActivitySubmissionField[] = [
  {
    id: "distrito",
    label: "Distrito",
    type: "text_short",
    required: true,
    order: 10,
  },
  {
    id: "iglesia",
    label: "Iglesia",
    type: "text_short",
    required: true,
    order: 20,
  },
  {
    id: "director",
    label: "Director del Club",
    type: "text_short",
    required: true,
    order: 30,
  },
  {
    id: "celular",
    label: "Número de celular del director",
    type: "text_short",
    required: true,
    order: 40,
  },
  {
    id: "pastor",
    label: "Pastor del Distrito",
    type: "text_short",
    required: true,
    order: 50,
  },
  {
    id: "total_directiva",
    label: "Total miembros directiva y consejeros",
    type: "number",
    required: true,
    min: 0,
    order: 60,
  },
  {
    id: "num_reuniones",
    label: "Número de reuniones durante el mes",
    type: "number",
    required: true,
    min: 0,
    order: 70,
  },
  {
    id: "porc_asistencia",
    label: "% de asistencia total del club en las reuniones",
    type: "number",
    required: true,
    min: 0,
    max: 100,
    helpText: "Valor entre 0 y 100",
    order: 80,
  },
  {
    id: "participo_conqui_dia",
    label: "¿Participaron como club en el evento Conquistador por un día?",
    type: "boolean",
    required: true,
    order: 90,
  },
  {
    id: "nuevos_contactos",
    label:
      "Durante 'Conquistador por un día', ¿hicieron contacto con nuevos niños?",
    type: "boolean",
    required: true,
    order: 100,
  },
  {
    id: "estudio_personal",
    label:
      "¿Realizan la estrategia de incentivar a los integrantes en el estudio personal?",
    type: "boolean",
    required: true,
    order: 110,
  },
  {
    id: "marchas_testificacion",
    label: "¿Como club participarán en el evento de marchas?",
    type: "boolean",
    required: true,
    order: 120,
  },
  {
    id: "lugar_marchas",
    label: "Lugar y hora donde realizarán la presentación de marchas",
    type: "text_long",
    required: false,
    rows: 2,
    order: 130,
  },
  {
    id: "marchas_campamento",
    label: "¿Como club presentarán marchas en el campamento?",
    type: "boolean",
    required: true,
    order: 140,
  },
  {
    id: "alimentacion_logistica",
    label:
      "¿Estarían dispuestos a brindar alimentación a un miembro de logística?",
    type: "boolean",
    required: true,
    order: 150,
  },
  {
    id: "num_logisticos_alimentar",
    label: "¿A cuántos integrantes de logística podrían brindar alimentación?",
    type: "number",
    required: false,
    min: 0,
    helpText: "Solo si respondió 'sí' a la pregunta anterior",
    order: 160,
  },
  {
    id: "evidencias",
    label: "Evidencias (fotos, videos)",
    type: "multi_file",
    required: false,
    acceptMimeTypes: ["image/*", "application/pdf"],
    maxFiles: 5,
    helpText: "Sube hasta 5 archivos",
    order: 170,
  },
];

/**
 * Plantilla: Video de Eventos Previos (un solo enlace o archivo).
 */
export const TEMPLATE_VIDEO_PREVIOS: ActivitySubmissionField[] = [
  {
    id: "video_url",
    label: "Enlace al video (YouTube, Drive, etc.)",
    type: "url",
    required: true,
    helpText: "Asegúrate de que sea accesible públicamente",
    order: 10,
  },
  {
    id: "descripcion",
    label: "Breve descripción del contenido",
    type: "text_long",
    required: false,
    rows: 3,
    order: 20,
  },
];

/**
 * Plantilla: Carpeta del Club (subida de PDF).
 */
export const TEMPLATE_CARPETA_CLUB: ActivitySubmissionField[] = [
  {
    id: "carpeta_pdf",
    label: "Archivo PDF de la carpeta del club",
    type: "file",
    required: true,
    acceptMimeTypes: ["application/pdf"],
    order: 10,
  },
  {
    id: "anexos",
    label: "Anexos adicionales",
    type: "multi_file",
    required: false,
    maxFiles: 10,
    order: 20,
  },
];

/**
 * Plantilla genérica: un único archivo (PDF o imagen). Útil para el menú
 * del campamento, planillas, soportes ad-hoc, etc.
 */
export const TEMPLATE_SINGLE_FILE: ActivitySubmissionField[] = [
  {
    id: "archivo",
    label: "Archivo",
    type: "file",
    required: true,
    acceptMimeTypes: [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    order: 10,
  },
  {
    id: "notas",
    label: "Notas (opcional)",
    type: "text_long",
    required: false,
    rows: 3,
    order: 20,
  },
];

/**
 * Plantilla genérica: un único enlace público (Drive, YouTube, etc.).
 */
export const TEMPLATE_SINGLE_URL: ActivitySubmissionField[] = [
  {
    id: "url",
    label: "Enlace público",
    type: "url",
    required: true,
    helpText:
      "Verifica permisos: cualquier persona con el link debe poder verlo.",
    order: 10,
  },
  {
    id: "notas",
    label: "Notas (opcional)",
    type: "text_long",
    required: false,
    rows: 3,
    order: 20,
  },
];

export const SUBMISSION_TEMPLATES = {
  informe_mensual: {
    label: "Informe Mensual de Conquistadores",
    fields: TEMPLATE_INFORME_MENSUAL,
  },
  video_previos: {
    label: "Video de Eventos Previos",
    fields: TEMPLATE_VIDEO_PREVIOS,
  },
  carpeta_club: {
    label: "Carpeta del Club",
    fields: TEMPLATE_CARPETA_CLUB,
  },
  single_file: {
    label: "Un archivo (PDF/imagen/xlsx)",
    fields: TEMPLATE_SINGLE_FILE,
  },
  single_url: {
    label: "Un enlace (URL)",
    fields: TEMPLATE_SINGLE_URL,
  },
} as const;

export type SubmissionTemplateKey = keyof typeof SUBMISSION_TEMPLATES;

// ============================================================================
// Etiquetas UI
// ============================================================================

export const SUBMISSION_FIELD_TYPE_LABEL: Record<
  ActivitySubmissionField["type"],
  string
> = {
  text_short: "Texto corto",
  text_long: "Texto largo",
  number: "Número",
  boolean: "Sí / No",
  select: "Selección única",
  multi_select: "Selección múltiple",
  date: "Fecha",
  url: "Enlace (URL)",
  file: "Archivo",
  multi_file: "Varios archivos",
};

export const SUBMISSION_STATUS_LABEL: Record<SubmissionStatus, string> = {
  draft: "Borrador",
  submitted: "Enviado",
  approved: "Aprobado",
  rejected: "Rechazado",
};

/**
 * Devuelve el "estado visible" para una entrega, combinando el flujo del
 * director (`status`) con el flujo del admin (`reviewStatus`):
 *  - Si la revisión es approved/rejected, gana sobre status.
 *  - Si no, mostramos el status (draft/submitted).
 */
export function getDisplaySubmissionStatus(
  s: Pick<ClubActivitySubmission, "status" | "reviewStatus">,
): SubmissionStatus {
  if (s.reviewStatus === "approved") return "approved";
  if (s.reviewStatus === "rejected") return "rejected";
  return s.status;
}
